import React, { useState, useEffect } from 'react';
import materialCatalogService from '../services/materialCatalogService';
import costService from '../services/costService';
import suppliersService from '../services/suppliersService';
import LoadingSpinner from './LoadingSpinner';
import ConfirmModal from './ConfirmModal';

// Categorias de materiais pré-definidas
const MATERIAL_CATEGORIES = [
  { value: 'MDF_BRANCO', label: 'MDF Branco' },
  { value: 'MDF_NORMAL', label: 'MDF Normal' },
  { value: 'MDF_ESPECIAL', label: 'MDF Especial (cores)' },
  { value: 'CORREDIÇAS', label: 'Corrediças' },
  { value: 'DOBRADIÇAS', label: 'Dobradiças' },
  { value: 'PARAFUSOS', label: 'Parafusos' },
  { value: 'PERFIS', label: 'Perfis e Puxadores' },
  { value: 'PONTÉIRAS', label: 'Pontéiras e Conectores' },
  { value: 'PVC', label: 'PVC e Fitas de Borda' },
  { value: 'COLA', label: 'Cola' },
  { value: 'THINNER', label: 'Thinner' },
  { value: 'ESTOPA', label: 'Estopa' },
  { value: 'MATERIA_PRIMA', label: 'Matéria Prima' },
  { value: 'FERRAGENS', label: 'Ferragens' },
  { value: 'INSUMOS', label: 'Insumos' },
  { value: 'SERVICOS', label: 'Serviços' },
  { value: 'LOGISTICA', label: 'Logística' },
  { value: 'OUTROS', label: 'Outros' }
];

const UNITS = [
  { value: 'un', label: 'Unidade' },
  { value: 'm', label: 'Metro' },
  { value: 'm2', label: 'Metro Quadrado' },
  { value: 'rolo', label: 'Rolo' },
  { value: 'litro', label: 'Litro' },
  { value: 'kg', label: 'Quilo' },
  { value: 'pct', label: 'Pacote' }
];

// Constantes para MDF
const MDF_LARGURA = 2.75;
const MDF_ALTURA = 1.85;

const MaterialCatalogManager = () => {
  const [materials, setMaterials] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [profitMargins, setProfitMargins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);

  const [formData, setFormData] = useState({
    tipo: 'material',
    nome: '',
    categoria: '',
    marca: '',
    especificacao: '',
    cor: '',
    unidade_medida: 'un',
    largura_chapa: MDF_LARGURA,
    comprimento_chapa: MDF_ALTURA,
    espessura_mm: '',
    preco_custo: '',
    margens_espessura: {},
    fornecedor_id: '',
    link_estoque: false,
    produto_inventario_id: '',
    observacoes: '',
    foto_url: '',
    sku: '',
    categoria_profit_margin: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadMaterials(),
        loadSuppliers(),
        loadProfitMargins()
      ]);
    } catch (error) {
      setError('Erro ao carregar dados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadMaterials = async () => {
    try {
      const response = await materialCatalogService.getAllMaterials({
        search: searchTerm,
        categoria: selectedCategory
      });
      setMaterials(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar materiais:', error);
    }
  };

  const loadSuppliers = async () => {
    try {
      const response = await suppliersService.getAllSuppliers();
      setSuppliers(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
    }
  };

  const loadProfitMargins = async () => {
    try {
      const response = await costService.getProfitMargins();
      setProfitMargins(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar margens:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadMaterials();
  };

  const handleCategoryFilter = (e) => {
    setSelectedCategory(e.target.value);
    loadMaterials();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      const dataToSend = {
        ...formData,
        preco_custo: formData.preco_custo ? parseFloat(formData.preco_custo) : null,
        espessura_mm: formData.espessura_mm ? parseFloat(formData.espessura_mm) : null,
        largura_chapa: formData.largura_chapa ? parseFloat(formData.largura_chapa) : null,
        comprimento_chapa: formData.comprimento_chapa ? parseFloat(formData.comprimento_chapa) : null,
        fornecedor_id: formData.fornecedor_id ? parseInt(formData.fornecedor_id) : null,
        link_estoque: formData.link_estoque || false
      };

      if (editingMaterial) {
        await materialCatalogService.updateMaterial(editingMaterial.id, dataToSend);
      } else {
        await materialCatalogService.createMaterial(dataToSend);
      }

      setShowForm(false);
      setEditingMaterial(null);
      resetForm();
      await loadMaterials();
    } catch (error) {
      console.error('Erro ao salvar material:', error);
      setError(error.response?.data?.message || 'Erro ao salvar material');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (material) => {
    setEditingMaterial(material);
    setFormData({
      tipo: material.tipo || 'material',
      nome: material.nome || '',
      categoria: material.categoria || '',
      marca: material.marca || '',
      especificacao: material.especificacao || '',
      cor: material.cor || '',
      unidade_medida: material.unidade_medida || 'un',
      largura_chapa: material.largura_chapa || MDF_LARGURA,
      comprimento_chapa: material.comprimento_chapa || MDF_ALTURA,
      espessura_mm: material.espessura_mm || '',
      preco_custo: material.preco_custo || '',
      margens_espessura: material.margens_espessura || {},
      fornecedor_id: material.fornecedor_id || '',
      link_estoque: material.link_estoque || false,
      produto_inventario_id: material.produto_inventario_id || '',
      observacoes: material.observacoes || '',
      foto_url: material.foto_url || '',
      sku: material.sku || '',
      categoria_profit_margin: material.categoria_profit_margin || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setConfirmMessage('Tem certeza que deseja excluir este material?');
    setConfirmAction(() => async () => {
      try {
        setLoading(true);
        setShowConfirmModal(false);
        await materialCatalogService.deleteMaterial(id);
        await loadMaterials();
      } catch (error) {
        console.error('Erro ao excluir material:', error);
        setError(error.response?.data?.message || 'Erro ao excluir material');
      } finally {
        setLoading(false);
      }
    });
    setShowConfirmModal(true);
  };

  const resetForm = () => {
    setFormData({
      tipo: 'material',
      nome: '',
      categoria: '',
      marca: '',
      especificacao: '',
      cor: '',
      unidade_medida: 'un',
      largura_chapa: MDF_LARGURA,
      comprimento_chapa: MDF_ALTURA,
      espessura_mm: '',
      preco_custo: '',
      margens_espessura: {},
      fornecedor_id: '',
      link_estoque: false,
      produto_inventario_id: '',
      observacoes: '',
      foto_url: '',
      sku: '',
      categoria_profit_margin: ''
    });
  };

  const openNewForm = () => {
    resetForm();
    setEditingMaterial(null);
    setShowForm(true);
  };

  const getMarginForCategory = (categoria) => {
    const margin = profitMargins.find(m =>
      m.category === categoria ||
      m.category === formData.categoria_profit_margin
    );
    return margin?.margin_percentage || 30;
  };

  const calculatePrice = () => {
    const custo = parseFloat(formData.preco_custo) || 0;
    const categoria = formData.categoria || formData.categoria_profit_margin;
    const margem = getMarginForCategory(categoria);

    if (formData.categoria?.includes('MDF') && formData.largura_chapa && formData.comprimento_chapa) {
      const area = (parseFloat(formData.largura_chapa) || MDF_LARGURA) * (parseFloat(formData.comprimento_chapa) || MDF_ALTURA);
      const custoPorM2 = custo / area;
      const vendaPorM2 = custoPorM2 * (1 + margem / 100);
      return {
        custoPorM2: custoPorM2.toFixed(2),
        vendaPorM2: vendaPorM2.toFixed(2),
        area: area.toFixed(2),
        margem
      };
    }

    const venda = custo * (1 + margem / 100);
    return {
      custoUnitario: custo.toFixed(2),
      vendaUnitaria: venda.toFixed(2),
      margem
    };
  };

  if (loading && materials.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Catálogo de Materiais</h1>
            <p className="text-gray-600">Gerencie materiais para orçamentos com margens de lucro</p>
          </div>
          <button
            onClick={openNewForm}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Novo Material
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Buscar materiais..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="w-48">
              <select
                value={selectedCategory}
                onChange={handleCategoryFilter}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas as categorias</option>
                {MATERIAL_CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              Buscar
            </button>
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
            <button onClick={() => setError(null)} className="float-right font-bold">&times;</button>
          </div>
        )}

        {/* Materials Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Especificação</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Custo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Venda</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Margem</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {materials.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    Nenhum material encontrado. Clique em "Novo Material" para adicionar.
                  </td>
                </tr>
              ) : (
                materials.map((material) => {
                  const margin = profitMargins.find(m =>
                    m.category === material.categoria_profit_margin ||
                    m.category === material.categoria
                  );
                  const margemPct = margin?.margin_percentage || 30;

                  let custoDisplay = '';
                  let vendaDisplay = '';

                  if (material.categoria?.includes('MDF') && material.custo_por_m2) {
                    custoDisplay = `R$ ${parseFloat(material.custo_por_m2).toFixed(2)}/m²`;
                    const venda = parseFloat(material.custo_por_m2) * (1 + margemPct / 100);
                    vendaDisplay = `R$ ${venda.toFixed(2)}/m²`;
                  } else {
                    custoDisplay = material.preco_custo ? `R$ ${parseFloat(material.preco_custo).toFixed(2)}` : '-';
                    const venda = material.preco_custo ? parseFloat(material.preco_custo) * (1 + margemPct / 100) : null;
                    vendaDisplay = venda ? `R$ ${venda.toFixed(2)}` : '-';
                  }

                  return (
                    <tr key={material.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{material.nome}</div>
                        {material.marca && <div className="text-sm text-gray-500">{material.marca}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                          {material.categoria}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {material.especificacao || material.cor || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {custoDisplay}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                        {vendaDisplay}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {margemPct}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleEdit(material)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(material.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Excluir
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">
                  {editingMaterial ? 'Editar Material' : 'Novo Material'}
                </h2>
                <button
                  onClick={() => { setShowForm(false); setEditingMaterial(null); }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Nome */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: MDF Branco 15mm"
                    />
                  </div>

                  {/* Categoria */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Categoria *</label>
                    <select
                      name="categoria"
                      value={formData.categoria}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione...</option>
                      {MATERIAL_CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Categoria Profit Margin */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Categoria p/ Margem</label>
                    <select
                      name="categoria_profit_margin"
                      value={formData.categoria_profit_margin}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Same as category</option>
                      {profitMargins.map(m => (
                        <option key={m.category} value={m.category}>
                          {m.category} ({m.margin_percentage}%)
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Marca */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                    <input
                      type="text"
                      name="marca"
                      value={formData.marca}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* SKU */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                    <input
                      type="text"
                      name="sku"
                      value={formData.sku}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Especificação */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Especificação</label>
                    <input
                      type="text"
                      name="especificacao"
                      value={formData.especificacao}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: 2750x1850mm"
                    />
                  </div>

                  {/* Cor */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cor</label>
                    <input
                      type="text"
                      name="cor"
                      value={formData.cor}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: Branco, Preto, Peroba"
                    />
                  </div>

                  {/* Espessura (para MDF) */}
                  {formData.categoria?.includes('MDF') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Espessura (mm)</label>
                      <input
                        type="number"
                        name="espessura_mm"
                        value={formData.espessura_mm}
                        onChange={handleChange}
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Ex: 15"
                      />
                    </div>
                  )}

                  {/* Dimensões (para MDF) */}
                  {formData.categoria?.includes('MDF') && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Largura (m)</label>
                        <input
                          type="number"
                          name="largura_chapa"
                          value={formData.largura_chapa}
                          onChange={handleChange}
                          step="0.001"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Comprimento (m)</label>
                        <input
                          type="number"
                          name="comprimento_chapa"
                          value={formData.comprimento_chapa}
                          onChange={handleChange}
                          step="0.001"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </>
                  )}

                  {/* Unidade de Medida */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unidade</label>
                    <select
                      name="unidade_medida"
                      value={formData.unidade_medida}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {UNITS.map(u => (
                        <option key={u.value} value={u.value}>{u.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Preço Custo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Preço Custo {formData.categoria?.includes('MDF') ? '(por chapas)' : ''}
                    </label>
                    <input
                      type="number"
                      name="preco_custo"
                      value={formData.preco_custo}
                      onChange={handleChange}
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                  </div>

                  {/* Fornecedor */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fornecedor</label>
                    <select
                      name="fornecedor_id"
                      value={formData.fornecedor_id}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione...</option>
                      {suppliers.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Link Estoque */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="link_estoque"
                      checked={formData.link_estoque}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <label className="ml-2 text-sm text-gray-700">Vincular ao estoque</label>
                  </div>

                  {/* Observações */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                    <textarea
                      name="observacoes"
                      value={formData.observacoes}
                      onChange={handleChange}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Price Preview */}
                {formData.preco_custo && (
                  <div className="mt-4 p-4 bg-green-50 rounded-lg">
                    <h4 className="font-medium text-green-800 mb-2">Preview de Preço</h4>
                    {(() => {
                      const priceCalc = calculatePrice();
                      if (formData.categoria?.includes('MDF')) {
                        return (
                          <div className="text-sm text-green-700">
                            <p>Custo por m²: R$ {priceCalc.custoPorM2}</p>
                            <p>Venda por m²: R$ {priceCalc.vendaPorM2}</p>
                            <p>Área da chapa: {priceCalc.area} m²</p>
                            <p>Margem aplicada: {priceCalc.margem}%</p>
                          </div>
                        );
                      }
                      return (
                        <div className="text-sm text-green-700">
                          <p>Custo unitário: R$ {priceCalc.custoUnitario}</p>
                          <p>Venda unitária: R$ {priceCalc.vendaUnitaria}</p>
                          <p>Margem aplicada: {priceCalc.margem}%</p>
                        </div>
                      );
                    })()}
                  </div>
                )}

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setEditingMaterial(null); }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Salvando...' : (editingMaterial ? 'Atualizar' : 'Criar')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={showConfirmModal}
        onConfirm={confirmAction}
        onCancel={() => setShowConfirmModal(false)}
        title="Confirmar Exclusão"
        message={confirmMessage}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default MaterialCatalogManager;
